// Straight from the date helper, not via collections — collections imports THIS file, and
// the reverse would be a cycle.
import { addDaysYmd } from './mcp/timezone';

/**
 * The routine chases a shop would otherwise forget, done on a timer.
 *
 * These are CANNED, not a rules builder. The owner this is for runs four to fifteen techs
 * and did not buy software to learn a trigger/condition/action grammar — every one of these
 * is a switch and at most one number. The whole surface is six switches, which is the
 * feature, not a limitation of it.
 *
 * Two of the six are older than this file: chasing an unpaid invoice on the day it is due
 * and again after it is late is what `invoice_policies.reminder_offset_days` has always
 * done, and it takes any list of offsets — before AND after — rather than the two fixed
 * steps. The Automations screen shows them alongside the rest so a shop reads one list, but
 * it edits the same policy row underneath. Two places to configure one behaviour is how a
 * shop ends up chasing a customer twice.
 */

/** Days after a quote goes out before the first and second nudge. */
export const QUOTE_FOLLOWUP_DEFAULT_DAYS = [2, 5] as const;

/** Days a sent quote sits unanswered before it is archived (expired). */
export const QUOTE_ARCHIVE_DEFAULT_DAYS = 30;

/** Days an untriaged request sits before it is archived. */
export const REQUEST_ARCHIVE_DEFAULT_DAYS = 30;

/** The bounds every day-count on this screen shares — matched by CHECK constraints. */
export const AUTOMATION_MIN_DAYS = 1;
export const AUTOMATION_MAX_DAYS = 365;

export interface PlannedStep {
  offsetDays: number;
  /** The date this step came due — the anchor plus its offset. */
  dueOn: string;
  /** Reached its date but overtaken by a later step in the same run. */
  superseded: boolean;
}

/**
 * Which timed steps are due today, and which have been overtaken.
 *
 * AT MOST ONE actually fires per run, and that is the whole reason this is one function
 * rather than two loops. Turn quote follow-ups on with a 30-day-old quote sitting there and
 * both steps are technically due at once — two emails to the same customer in one night,
 * about the same quote. The LATEST applicable step wins because it is the truest description
 * of the situation; the ones it overtook are recorded as superseded so they can never fire
 * on a later night either.
 *
 * Extracted from the invoice dunning sweep, which had this rule and needed it for exactly
 * the same reason. Quote follow-ups reuse it rather than re-deriving it, so the two cannot
 * drift into disagreeing about what "already chased" means.
 */
export function dueSteps(
  offsets: readonly number[],
  anchorYmd: string,
  alreadyHandled: readonly number[],
  today: string,
): PlannedStep[] {
  const handled = new Set(alreadyHandled);
  const due = offsets
    .filter((o) => !handled.has(o))
    .map((offsetDays) => ({ offsetDays, dueOn: addDaysYmd(anchorYmd, offsetDays) }))
    .filter((r) => r.dueOn <= today)
    .sort((a, b) => a.offsetDays - b.offsetDays);
  if (due.length === 0) return [];

  const winner = due[due.length - 1]!;
  return due.map((r) => ({ ...r, superseded: r.offsetDays !== winner.offsetDays }));
}

/**
 * "1 day" / "30 days".
 *
 * Every automation on this screen is configured in days and every one of them reports back
 * in a sentence a shop owner reads, so the `n === 1` branch was about to be written a fourth
 * time — and had already been got wrong once ("no answer in 1 days").
 */
export function dayCount(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

/** How a quote follow-up step reads to a person: "2 days after it was sent". */
export function quoteFollowupLabel(offsetDays: number): string {
  return `${dayCount(offsetDays)} after it was sent`;
}
